import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  ArrowLeft, Clock, Camera, Wrench, Euro, PenLine, FileText, Plus, X, Play, Square,
  AlertTriangle, ShieldCheck, Trash2, Search, Check, Edit2, MessageCircle, Send, PackageMinus
} from "lucide-react";

const STATI = ["aperta", "in_lavorazione", "in_attesa_ricambi", "completata", "fatturata", "chiusa"];

// Stampa "Scheda Lavoro" cartacea (ordine di lavoro) da lasciare sul mezzo
async function printWorkSheet(wo, machine) {
  // Load logo
  let logoData = null;
  try {
    const resp = await fetch("/assets/germac-logo.png");
    const blob = await resp.blob();
    logoData = await new Promise(res => { const r = new FileReader(); r.onload = ()=>res(r.result); r.readAsDataURL(blob); });
  } catch (e) { /* logo opzionale */ }

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210, M = 12;
  const C_DARK = [25, 28, 34];
  const C_GREEN = [76, 175, 80];
  const C_LINE = [180, 180, 180];

  // Header
  doc.setFillColor(...C_DARK); doc.rect(0, 0, W, 26, "F");
  if (logoData) {
    doc.setFillColor(255,255,255).roundedRect(M, 4, 18, 18, 1, 1, "F");
    doc.addImage(logoData, "PNG", M+2, 6, 14, 14);
  }
  doc.setTextColor(255,255,255).setFont("helvetica", "bold").setFontSize(16);
  doc.text("SCHEDA LAVORO / ORDINE DI LAVORO", M+22, 12);
  doc.setFontSize(8).setFont("helvetica", "normal").setTextColor(200,200,210);
  doc.text("GERMAC S.R.L. — Da lasciare sul mezzo in riparazione", M+22, 17);
  doc.setTextColor(...C_GREEN).setFont("helvetica", "bold").setFontSize(13);
  doc.text(wo.numero, W-M, 12, { align: "right" });
  doc.setTextColor(200,200,210).setFont("helvetica", "normal").setFontSize(8);
  doc.text(`APERTA IL: ${new Date(wo.created_at).toLocaleDateString("it-IT")}`, W-M, 18, { align: "right" });
  doc.text(`URGENZA: ${(wo.urgenza||"").toUpperCase()}`, W-M, 22, { align: "right" });
  doc.setFillColor(...C_GREEN).rect(0, 26, W, 1.5, "F");

  let y = 34;

  // Cliente + Macchina (2 colonne)
  doc.setDrawColor(...C_LINE).setLineWidth(0.3);
  const cw = (W - M*2 - 4) / 2;
  doc.rect(M, y, cw, 34);
  doc.setFillColor(240,240,240).rect(M, y, cw, 5, "F"); doc.setDrawColor(...C_LINE).rect(M, y, cw, 34);
  doc.setTextColor(...C_DARK).setFont("helvetica", "bold").setFontSize(8);
  doc.text("CLIENTE", M+2, y+3.5);
  doc.setFontSize(10).text(wo.customer_name || "—", M+2, y+10);
  doc.setFont("helvetica", "normal").setFontSize(8);
  let yc = y+15;
  if (machine?.customer?.referente) { doc.text(`Referente: ${machine.customer.referente}`, M+2, yc); yc+=4; }
  if (machine?.customer?.telefono) { doc.text(`Tel: ${machine.customer.telefono}`, M+2, yc); yc+=4; }
  if (machine?.customer?.email) { doc.text(machine.customer.email, M+2, yc); yc+=4; }
  if (machine?.customer?.indirizzo) { doc.text(machine.customer.indirizzo.substring(0,45), M+2, yc); }

  const xM = M + cw + 4;
  doc.rect(xM, y, cw, 34);
  doc.setFillColor(240,240,240).rect(xM, y, cw, 5, "F"); doc.setDrawColor(...C_LINE).rect(xM, y, cw, 34);
  doc.setFont("helvetica", "bold").setFontSize(8).text("MACCHINA", xM+2, y+3.5);
  doc.setFontSize(10).text(`${machine?.marca || ""} ${machine?.modello || ""}`.trim() || "—", xM+2, y+10);
  doc.setFont("helvetica", "normal").setFontSize(8);
  doc.text(`Matricola: ${machine?.matricola || "—"}`, xM+2, y+15);
  if (machine?.targa) doc.text(`Targa: ${machine.targa}`, xM+2, y+19);
  doc.text(`Ore lavoro attuali: ${machine?.ore_lavoro || 0}h`, xM+2, y+23);
  const inW = machine?.garanzia_scadenza && new Date(machine.garanzia_scadenza) >= new Date();
  doc.setTextColor(inW ? 76 : 90, inW ? 175 : 90, inW ? 80 : 90);
  doc.text(`Garanzia: ${machine?.garanzia_scadenza ? (inW?"ATTIVA fino al ":"scaduta il ")+machine.garanzia_scadenza : "n/d"}`, xM+2, y+27);
  doc.setTextColor(...C_DARK);
  doc.text(`Tipo intervento: ${(wo.tipo_garanzia||"").replaceAll("_"," ").toUpperCase()}`, xM+2, y+31);

  y += 40;

  // TRASFERTA / LUOGO INTERVENTO (visibile se compilato)
  const hasTrasferta = wo.luogo_intervento || wo.km_viaggio || wo.tempo_viaggio_min;
  if (hasTrasferta) {
    doc.setFillColor(255, 249, 234); doc.rect(M, y, W-M*2, 12, "F"); // sfondo giallo tenue
    doc.setDrawColor(...C_LINE).setLineWidth(0.3).rect(M, y, W-M*2, 12);
    doc.setFont("helvetica", "bold").setFontSize(8).setTextColor(...C_DARK);
    doc.text("TRASFERTA", M+2, y+3.5);
    doc.setFont("helvetica", "normal").setFontSize(9);
    // riga con luogo
    if (wo.luogo_intervento) {
      doc.text(`Luogo: ${wo.luogo_intervento.substring(0, 90)}`, M+2, y+8);
    }
    // km e tempo (destra)
    const km = wo.km_viaggio ? `${wo.km_viaggio} km` : "—";
    const tv = wo.tempo_viaggio_min ? (wo.tempo_viaggio_min >= 60
      ? `${Math.floor(wo.tempo_viaggio_min/60)}h ${wo.tempo_viaggio_min%60}min`
      : `${wo.tempo_viaggio_min} min`) : "—";
    doc.setFont("helvetica", "bold").setFontSize(8);
    doc.text(`KM (A/R): ${km}   ·   TEMPO VIAGGIO: ${tv}`, W-M, y+8, { align: "right" });
    y += 16;
  }

  // PROBLEMA RIFERITO
  doc.setFont("helvetica", "bold").setFontSize(10).setTextColor(...C_DARK);
  doc.text("PROBLEMA RIFERITO DAL CLIENTE", M, y);
  doc.setDrawColor(...C_GREEN).setLineWidth(0.8); doc.line(M, y+1.5, M+70, y+1.5);
  y += 5;
  doc.setDrawColor(...C_LINE).setLineWidth(0.3);
  doc.rect(M, y, W-M*2, 20);
  doc.setFont("helvetica", "normal").setFontSize(9);
  const problem = doc.splitTextToSize(wo.descrizione_problema || "—", W-M*2-4);
  doc.text(problem.slice(0,4), M+2, y+5);
  y += 24;

  // LAVORO SVOLTO - righe vuote per scrivere
  doc.setFont("helvetica", "bold").setFontSize(10);
  doc.text("LAVORO SVOLTO DAL MECCANICO", M, y);
  doc.setDrawColor(...C_GREEN).setLineWidth(0.8); doc.line(M, y+1.5, M+70, y+1.5);
  y += 4;
  doc.setDrawColor(...C_LINE).setLineWidth(0.2);
  for (let i = 0; i < 10; i++) { doc.line(M, y + (i+1)*6, W-M, y + (i+1)*6); }
  y += 66;

  // MATERIALE UTILIZZATO - tabella vuota
  doc.setFont("helvetica", "bold").setFontSize(10);
  doc.text("MATERIALE / RICAMBI UTILIZZATI", M, y);
  doc.setDrawColor(...C_GREEN).setLineWidth(0.8); doc.line(M, y+1.5, M+70, y+1.5);
  y += 4;
  const cols = [ ["Codice / Descrizione", 100], ["Q.tà", 20], ["Note", W-M*2-120] ];
  doc.setFillColor(240,240,240); doc.rect(M, y, W-M*2, 6, "F");
  doc.setDrawColor(...C_LINE).setLineWidth(0.3).rect(M, y, W-M*2, 6);
  doc.setFont("helvetica", "bold").setFontSize(8).setTextColor(...C_DARK);
  let cx = M+1;
  cols.forEach(([label, w]) => { doc.text(label, cx+1, y+4); cx += w; doc.line(cx, y, cx, y+6); });
  y += 6;
  for (let i = 0; i < 7; i++) {
    doc.setDrawColor(...C_LINE).rect(M, y, W-M*2, 6);
    let cx2 = M;
    cols.forEach(([, w]) => { cx2 += w; doc.line(cx2, y, cx2, y+6); });
    y += 6;
  }

  y += 4;
  // ORE LAVORATE
  doc.setFont("helvetica", "bold").setFontSize(10);
  doc.text("ORE LAVORATE", M, y);
  doc.setDrawColor(...C_GREEN).setLineWidth(0.8); doc.line(M, y+1.5, M+30, y+1.5);
  y += 4;
  const c2 = [ ["Data", 30], ["Tecnico", 60], ["Dalle", 25], ["Alle", 25], ["Ore tot.", 25] ];
  doc.setFillColor(240,240,240); doc.rect(M, y, W-M*2, 6, "F");
  doc.setDrawColor(...C_LINE).rect(M, y, W-M*2, 6);
  doc.setFont("helvetica", "bold").setFontSize(8);
  let cx3 = M+1;
  c2.forEach(([label, w]) => { doc.text(label, cx3+1, y+4); cx3 += w; doc.line(cx3, y, cx3, y+6); });
  y += 6;
  for (let i = 0; i < 4; i++) {
    doc.rect(M, y, W-M*2, 6);
    let cx4 = M;
    c2.forEach(([, w]) => { cx4 += w; doc.line(cx4, y, cx4, y+6); });
    y += 6;
  }

  y += 6;
  // Firme
  doc.setDrawColor(...C_LINE);
  doc.line(M, y+15, M+75, y+15);
  doc.setFont("helvetica", "normal").setFontSize(8).setTextColor(90,90,90);
  doc.text("FIRMA MECCANICO", M, y+19);
  doc.line(W-M-75, y+15, W-M, y+15);
  doc.text("FIRMA CLIENTE (ritiro macchina)", W-M-75, y+19);

  doc.setFontSize(7).setTextColor(120,120,120);
  doc.text(`GERMAC S.R.L. — Ordine di Lavoro ${wo.numero} — Stampato il ${new Date().toLocaleString("it-IT")}`, W/2, 290, { align: "center" });

  doc.save(`OdL_${wo.numero}.pdf`);
}


export default function WorkOrderDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [wo, setWo] = useState(null);
  const [machine, setMachine] = useState(null);
  const [tab, setTab] = useState("info");
  const [users, setUsers] = useState([]);
  const [waPreview, setWaPreview] = useState(null); // null | "loading" | { message, to_e164, cliente, configured, can_send }
  const [waSending, setWaSending] = useState(false);

  useEffect(() => { api.get("/users").then(r=>setUsers(r.data)).catch(()=>{}); }, []);

  useEffect(() => {
    if (waPreview === "loading") {
      api.get(`/notifications/whatsapp/preview/${id}`)
        .then(r => {
          const p = r.data;
          // Se ci sono forwarders configurati, default = primo forwarder (uso interno / inoltro manuale)
          if ((p.forwarders || []).length > 0) {
            p.to_e164 = p.forwarders[0].phone;
          }
          setWaPreview(p);
        })
        .catch(err => { toast.error(err.response?.data?.detail || "Errore anteprima"); setWaPreview(null); });
    }
  }, [waPreview, id]);

  const sendWhatsApp = async () => {
    if (!waPreview || waPreview === "loading") return;
    setWaSending(true);
    try {
      const { data } = await api.post("/notifications/whatsapp/send", {
        wo_id: id,
        to_e164: waPreview.to_e164,
        message: waPreview.message,
      });
      toast.success(`WhatsApp inviato (${data.status})`);
      setWaPreview(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio WhatsApp");
    } finally { setWaSending(false); }
  };

  const load = async () => {
    const { data } = await api.get(`/work-orders/${id}`);
    setWo(data);
    if (data?.machine_id) api.get(`/machines/${data.machine_id}`).then(r => setMachine(r.data)).catch(()=>{});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (wo && searchParams.get("wa") === "1") {
      setWaPreview("loading");
      searchParams.delete("wa");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line
  }, [wo]);

  if (!wo) return <div className="p-8 text-zinc-500">Caricamento...</div>;

  const updateField = async (field, value) => {
    try {
      const { data } = await api.put(`/work-orders/${id}`, { [field]: value });
      setWo(data);
      toast.success("Aggiornato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const myActive = wo.time_entries?.find(t => t.user_id === user.id && !t.check_out);

  const checkin = async () => {
    try { const { data } = await api.post(`/work-orders/${id}/checkin`); setWo(data); toast.success("Check-in registrato"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const checkout = async () => {
    try { const { data } = await api.post(`/work-orders/${id}/checkout`, {}); setWo(data); toast.success(`Check-out. Ore registrate.`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto pb-24">
      <button onClick={()=>nav(-1)} className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white mb-3 flex items-center gap-1.5">
        <ArrowLeft size={14} /> Indietro
      </button>

      <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
        <div>
          <div className="font-mono-data text-sm text-[#4CAF50] mb-1">{wo.numero}</div>
          <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">{wo.machine_label}</h1>
          <div className="text-zinc-400 mt-1">{wo.customer_name}</div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <select value={wo.stato} onChange={e=>updateField("stato", e.target.value)} className="gm-input h-10 w-52" data-testid="wo-stato-select">
            {STATI.map(s=><option key={s} value={s}>{s.replaceAll("_"," ").toUpperCase()}</option>)}
          </select>
          <div className="flex gap-2">
            <button onClick={()=>printWorkSheet(wo, machine)} className="h-9 px-3 border border-zinc-700 hover:border-[#4CAF50] uppercase text-xs tracking-widest font-bold text-zinc-300 hover:text-[#4CAF50] flex items-center gap-1.5" data-testid="wo-print-worksheet">
              <FileText size={14} /> Stampa scheda lavoro
            </button>
            {(user?.role === "admin" || user?.role === "ufficio_acquisti") && (
              <button onClick={async () => {
                if (!window.confirm(`Eliminare definitivamente la scheda ${wo.numero}? L'operazione è irreversibile.`)) return;
                try { await api.delete(`/work-orders/${id}`); toast.success("Scheda eliminata"); nav("/schede"); }
                catch (e) { toast.error(e.response?.data?.detail || "Errore eliminazione"); }
              }} className="h-9 px-3 border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 uppercase text-xs tracking-widest font-bold flex items-center gap-1.5" data-testid="wo-delete-button">
                <Trash2 size={14} /> Elimina
              </button>
            )}
          </div>
          {wo.urgenza === "critica" && <span className="gm-badge gm-badge-danger flex items-center gap-1"><AlertTriangle size={12}/> URGENTE</span>}
        </div>
      </div>

      {/* Quick check-in bar */}
      <div className="gm-card p-4 mb-5 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Clock className="text-[#4CAF50]" size={20} />
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-500">Tuo intervento</div>
            <div className="font-semibold">
              {myActive ? `In corso da ${new Date(myActive.check_in).toLocaleString("it-IT")}` : "Non attivo"}
            </div>
          </div>
        </div>
        {myActive ? (
          <button onClick={checkout} className="h-11 px-5 bg-[#FF3B30] text-white uppercase tracking-widest font-bold flex items-center gap-2 rounded-sm" data-testid="wo-checkout">
            <Square size={16} /> Check-out
          </button>
        ) : (
          <button onClick={checkin} className="gm-btn-primary h-11 px-5 rounded-sm flex items-center gap-2" data-testid="wo-checkin">
            <Play size={16} /> Check-in (avvia)
          </button>
        )}
        <button onClick={()=>nav(`/smontaggi?nuovo=1&wo=${wo.id}&wo_numero=${wo.numero}`)} className="h-11 px-4 border border-[#FF9F0A]/50 text-[#FF9F0A] uppercase tracking-widest text-xs font-bold flex items-center gap-2 rounded-sm hover:bg-[#FF9F0A]/10" data-testid="wo-smontaggio-btn">
          <PackageMinus size={15} /> Smontaggio mezzo nuovo
        </button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="Ore lavorate" value={`${(wo.ore_totali||0).toFixed(1)}h`} />
        <StatCard label="Ricambi (€)" value={`€ ${(wo.ricambi_costo||0).toFixed(2)}`} />
        <StatCard label="Manodopera (€)" value={`€ ${(wo.manodopera_costo||0).toFixed(2)}`} />
        <StatCard label="TOTALE" value={`€ ${(wo.totale||0).toFixed(2)}`} highlight />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-zinc-800 overflow-x-auto">
        {[
          ["info", "Info & Problema"],
          ["ricambi", `Ricambi (${wo.ricambi?.length||0})`],
          ["tempi", `Tempi (${wo.time_entries?.length||0})`],
          ["foto", "Foto"],
          ["costi", "Costi & Firma"],
        ].map(([k,l])=>(
          <button key={k} onClick={()=>setTab(k)} className={`px-4 py-3 text-xs uppercase tracking-widest font-bold whitespace-nowrap ${tab===k?"text-[#4CAF50] border-b-2 border-[#4CAF50]":"text-zinc-500 hover:text-white"}`} data-testid={`wo-tab-${k}`}>{l}</button>
        ))}
      </div>

      {tab === "info" && <InfoTab wo={wo} machine={machine} updateField={updateField} setWaPreview={setWaPreview} users={users} />}
      {tab === "ricambi" && <PartsTab wo={wo} reload={load} />}
      {tab === "tempi" && <TimeTab wo={wo} reload={load} />}
      {tab === "foto" && <PhotosTab wo={wo} reload={load} />}
      {tab === "costi" && <CostsTab wo={wo} machine={machine} updateField={updateField} reload={load} />}

      {/* Modal WhatsApp */}
      {waPreview && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="wa-modal">
          <div className="gm-card p-5 w-full max-w-lg my-8">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="h-9 w-9 bg-[#25D366]/15 border border-[#25D366]/50 flex items-center justify-center rounded-sm"><MessageCircle className="text-[#25D366]" size={18}/></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#25D366] font-bold">Notifica cliente</div>
                  <h3 className="font-display text-xl font-black uppercase">WhatsApp</h3>
                </div>
              </div>
              <button onClick={()=>setWaPreview(null)} className="text-zinc-400 hover:text-white p-1"><X size={20}/></button>
            </div>

            {waPreview === "loading" ? (
              <div className="text-sm text-zinc-400 py-6 text-center">Preparazione anteprima...</div>
            ) : (
              <>
                {!waPreview.configured && (
                  <div className="mb-3 flex items-start gap-2 text-xs text-yellow-400 bg-yellow-500/5 border border-yellow-500/30 p-2 rounded-sm">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0"/>
                    <div>Twilio non configurato. Aggiungi <code className="font-mono-data">TWILIO_ACCOUNT_SID</code>, <code className="font-mono-data">TWILIO_AUTH_TOKEN</code> e <code className="font-mono-data">TWILIO_WHATSAPP_FROM</code> in <code className="font-mono-data">backend/.env</code>.</div>
                  </div>
                )}
                {!waPreview.to_e164 && (
                  <div className="mb-3 flex items-start gap-2 text-xs text-[#FF3B30] bg-[#FF3B30]/5 border border-[#FF3B30]/40 p-2 rounded-sm">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0"/>
                    <div>Numero cliente mancante o non valido. Aggiungi/correggi il telefono nella scheda cliente.</div>
                  </div>
                )}
                <div className="text-xs text-zinc-500 mb-1">Invia a:</div>
                <div className="grid grid-cols-1 gap-2 mb-3">
                  <select
                    value={waPreview.to_e164 || ""}
                    onChange={e=>setWaPreview(p=>({...p, to_e164: e.target.value}))}
                    className="gm-input"
                    data-testid="wa-recipient-select"
                  >
                    {waPreview.customer_e164 && (
                      <option value={waPreview.customer_e164}>Cliente: {waPreview.cliente} ({waPreview.customer_e164})</option>
                    )}
                    {(waPreview.forwarders || []).map(f => (
                      <option key={f.phone} value={f.phone}>Inoltra a {f.name} ({f.phone})</option>
                    ))}
                  </select>
                </div>
                <div className="text-xs text-zinc-500 mb-1">Messaggio:</div>
                <textarea
                  rows={7}
                  value={waPreview.message}
                  onChange={e=>setWaPreview(p=>({...p, message: e.target.value}))}
                  className="gm-input font-mono-data text-xs whitespace-pre-wrap"
                  data-testid="wa-message"
                />
                <div className="flex gap-2 mt-3 pt-3 border-t border-zinc-800">
                  <button
                    onClick={sendWhatsApp}
                    disabled={!waPreview.can_send || waSending}
                    className="gm-btn-primary h-11 px-5 rounded-sm flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{background: waPreview.can_send ? "#25D366" : undefined}}
                    data-testid="wa-send-btn"
                  >
                    <Send size={16}/> {waSending ? "Invio..." : "Invia ora"}
                  </button>
                  <button onClick={()=>setWaPreview(null)} className="h-11 px-4 border border-zinc-700 uppercase text-xs tracking-widest font-bold">Annulla</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const StatCard = ({label, value, highlight}) => (
  <div className={`gm-card p-4 ${highlight?"border-[#4CAF50]/40 bg-[#4CAF50]/5":""}`}>
    <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
    <div className={`font-display text-2xl font-black mt-1 ${highlight?"text-[#4CAF50]":""}`}>{value}</div>
  </div>
);

const TariffaKmField = () => {
  const { user } = useAuth();
  const [tariffa, setTariffa] = useState(null);
  useEffect(() => { api.get("/settings/tariffa-km").then(({data}) => setTariffa(data.tariffa_km)).catch(()=>{}); }, []);
  const canEdit = ["admin", "ufficio_acquisti"].includes(user?.role);
  const save = async (v) => {
    const val = parseFloat(String(v).replace(",", "."));
    if (isNaN(val) || val < 0) return;
    try { const { data } = await api.put("/settings/tariffa-km", { tariffa_km: val }); setTariffa(data.tariffa_km); toast.success("Tariffa €/km aggiornata"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-zinc-500">Tariffa (€/km)</label>
      {canEdit ? (
        <input key={tariffa} type="number" step="0.01" min="0" defaultValue={tariffa ?? ""} onBlur={e=>save(e.target.value)} className="gm-input font-mono-data" data-testid="wo-tariffa-km" />
      ) : (
        <div className="gm-input font-mono-data opacity-70" data-testid="wo-tariffa-km-readonly">€ {(tariffa ?? 0).toFixed(2)}</div>
      )}
    </div>
  );
};

const InfoTab = ({wo, machine, updateField, setWaPreview, users}) => (
  <div className="space-y-5">
    <div className="gm-card p-5">
      <h3 className="font-display text-xl font-bold uppercase tracking-tight mb-3">Segnalazione cliente</h3>
      <textarea defaultValue={wo.descrizione_problema} onBlur={e=>e.target.value!==wo.descrizione_problema && updateField("descrizione_problema", e.target.value)} rows="4" className="gm-input py-2 h-auto resize-none w-full" data-testid="wo-info-desc" />
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-4">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500">Tipologia</label>
          <select defaultValue={wo.tipologia_intervento || "officina"} onChange={e=>updateField("tipologia_intervento", e.target.value)} className="gm-input" data-testid="wo-info-tipologia">
            <option value="officina">Officina</option>
            <option value="cantiere">Cantiere</option>
            <option value="emergenza">Emergenza</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500">Urgenza</label>
          <select defaultValue={wo.urgenza} onChange={e=>updateField("urgenza", e.target.value)} className="gm-input">
            {["bassa","media","alta","critica"].map(u=><option key={u} value={u}>{u.toUpperCase()}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500">Tipo intervento</label>
          <select defaultValue={wo.tipo_garanzia} onChange={e=>updateField("tipo_garanzia", e.target.value)} className="gm-input">
            <option value="pagamento">A Pagamento</option>
            <option value="garanzia_cliente">Garanzia Cliente</option>
            <option value="garanzia_costruttore">Garanzia Costruttore</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500">Data prevista</label>
          <div className="flex gap-2">
            <input type="date" defaultValue={wo.data_prevista} onBlur={e=>updateField("data_prevista", e.target.value)} className="gm-input flex-1" data-testid="wo-data-prevista" />
            <button
              type="button"
              onClick={()=>setWaPreview("loading")}
              disabled={!wo.data_prevista}
              title="Notifica cliente via WhatsApp"
              className="h-12 w-12 border border-zinc-700 hover:border-[#25D366] hover:text-[#25D366] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center shrink-0"
              data-testid="wo-whatsapp-btn"
            >
              <MessageCircle size={16}/>
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500">Data ingresso macchina</label>
          <input type="date" defaultValue={wo.data_ingresso || ""} onBlur={e=>updateField("data_ingresso", e.target.value)} className="gm-input" data-testid="wo-data-ingresso" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500">Preso in carico da</label>
          <select defaultValue={wo.preso_in_carico_da_id || ""} onChange={e=>updateField("preso_in_carico_da_id", e.target.value)} className="gm-input" data-testid="wo-preso-in-carico">
            <option value="">— Non assegnato</option>
            {(users || []).map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
          </select>
        </div>
      </div>

      {/* Trasferta (visibile utile soprattutto per cantiere/emergenza) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 pt-3 border-t border-zinc-800">
        <div className="md:col-span-3 flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
          Trasferta / Cantiere
        </div>
        <div className="md:col-span-3">
          <label className="text-[10px] uppercase tracking-widest text-zinc-500">Luogo intervento</label>
          <input type="text" defaultValue={wo.luogo_intervento || ""} onBlur={e=>updateField("luogo_intervento", e.target.value)} className="gm-input" placeholder="Es. Via Roma 10, Milano — cantiere Rossi" data-testid="wo-luogo-intervento" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500">Km viaggio (A/R)</label>
          <input type="number" step="0.1" min="0" defaultValue={wo.km_viaggio || 0} onBlur={e=>updateField("km_viaggio", parseFloat(e.target.value)||0)} className="gm-input font-mono-data" data-testid="wo-km-viaggio" />
        </div>
        <TariffaKmField />
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500">Tempo di viaggio (minuti, A/R)</label>
          <input type="number" step="1" min="0" defaultValue={wo.tempo_viaggio_min || 0} onBlur={e=>updateField("tempo_viaggio_min", parseFloat(e.target.value)||0)} className="gm-input font-mono-data" data-testid="wo-tempo-viaggio" />
        </div>
        <div className="md:col-span-3 text-[11px] text-zinc-500 -mt-1">
          Il <b className="text-zinc-300">costo trasferta</b> viene calcolato automaticamente: km × tariffa. Attuale: <span className="font-mono-data text-[#4CAF50]" data-testid="wo-rimborso-km-preview">€ {(wo.costo_trasferta||0).toFixed(2)}</span> (modificabile manualmente nel tab Costi)
        </div>
      </div>

      {/* Fatturazione */}
      {wo.tipo_garanzia === "pagamento" && (["completata","chiusa","fatturata"].includes(wo.stato)) && (
        <div className={`mt-4 p-3 border ${wo.fatturato ? "border-[#4CAF50]/40 bg-[#4CAF50]/5" : "border-[#FF3B30]/40 bg-[#FF3B30]/10"}`} data-testid="wo-fatturazione-panel">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <span className={`text-xs uppercase tracking-widest font-bold ${wo.fatturato ? "text-[#4CAF50]" : "text-[#FF3B30]"}`}>
                {wo.fatturato ? "✓ FATTURATA" : "⚠ DA FATTURARE"}
              </span>
              {wo.numero_fattura && <span className="font-mono-data text-xs text-zinc-300">n° {wo.numero_fattura}</span>}
              {wo.data_fatturazione && <span className="text-xs text-zinc-400">{wo.data_fatturazione}</span>}
            </div>
            {!wo.fatturato && (
              <div className="flex items-center gap-2 flex-wrap">
                <input placeholder="N° fattura (opzionale)" onBlur={e=> e.target.value && updateField("numero_fattura", e.target.value)}
                  className="gm-input h-9 w-40 text-sm font-mono-data" data-testid="wo-numero-fattura-input" />
                <button
                  onClick={()=>{
                    updateField("fatturato", true);
                    updateField("data_fatturazione", new Date().toISOString().slice(0,10));
                  }}
                  data-testid="wo-mark-fatturata"
                  className="gm-btn-primary h-9 px-4 rounded-sm text-xs"
                >Segna come Fatturata</button>
              </div>
            )}
            {wo.fatturato && (
              <button onClick={()=>{ updateField("fatturato", false); updateField("data_fatturazione", ""); }} className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white">
                Annulla fatturazione
              </button>
            )}
          </div>
        </div>
      )}
    </div>

    {machine && (
      <div className="gm-card p-5">
        <h3 className="font-display text-xl font-bold uppercase tracking-tight mb-3">Macchina</h3>
        <dl className="grid grid-cols-1 md:grid-cols-3 gap-y-2 gap-x-6 text-sm">
          <Row k="Marca" v={machine.marca} />
          <Row k="Modello" v={machine.modello} />
          <Row k="Matricola" v={<span className="font-mono-data">{machine.matricola}</span>} />
          <Row k="Targa" v={machine.targa || "—"} />
          <MachineOreInline machine={machine} />
          <Row k="Garanzia scade" v={machine.garanzia_scadenza ? <span className={new Date(machine.garanzia_scadenza)>=new Date()?"text-[#4CAF50]":"text-zinc-400"}>{machine.garanzia_scadenza}</span> : "—"} />
        </dl>
        {wo.customer_name && machine.customer && (
          <div className="mt-4 pt-4 border-t border-zinc-800 text-sm">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Contatto cliente</div>
            <div>{machine.customer.referente} {machine.customer.telefono && <span className="ml-3 text-zinc-400">{machine.customer.telefono}</span>}</div>
          </div>
        )}
      </div>
    )}

    <div className="gm-card p-5">
      <h3 className="font-display text-xl font-bold uppercase tracking-tight mb-3">Note tecniche & Attività svolte</h3>
      <label className="text-[10px] uppercase tracking-widest text-zinc-500">Attività svolte</label>
      <textarea defaultValue={wo.attivita_svolte} onBlur={e=>updateField("attivita_svolte", e.target.value)} rows="3" className="gm-input py-2 h-auto resize-none w-full mb-3" data-testid="wo-attivita" />
      <label className="text-[10px] uppercase tracking-widest text-zinc-500">Note tecniche</label>
      <textarea defaultValue={wo.note_tecniche} onBlur={e=>updateField("note_tecniche", e.target.value)} rows="3" className="gm-input py-2 h-auto resize-none w-full" data-testid="wo-note-tec" />
    </div>
  </div>
);

const Row = ({k,v}) => (
  <div className="flex flex-col"><dt className="text-[10px] uppercase tracking-widest text-zinc-500">{k}</dt><dd className="text-white">{v}</dd></div>
);

const MachineOreInline = ({machine}) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(machine.ore_lavoro || 0);
  useEffect(()=>setVal(machine.ore_lavoro || 0), [machine.ore_lavoro]);
  const save = async () => {
    try {
      await api.patch(`/machines/${machine.id}/ore`, { ore_lavoro: parseFloat(val) || 0 });
      machine.ore_lavoro = parseFloat(val) || 0;
      toast.success("Ore macchina aggiornate");
      setEditing(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="flex flex-col">
      <dt className="text-[10px] uppercase tracking-widest text-zinc-500">Ore lavoro macchina</dt>
      <dd className="text-white flex items-center gap-2">
        {editing ? (
          <>
            <input type="number" step="0.1" value={val} onChange={e=>setVal(e.target.value)}
              className="bg-transparent border-b border-[#4CAF50] outline-none font-mono-data w-24 text-base"
              autoFocus data-testid="machine-ore-input" />
            <button onClick={save} className="text-xs uppercase tracking-widest text-[#4CAF50]" data-testid="machine-ore-save">Salva</button>
            <button onClick={()=>{setVal(machine.ore_lavoro||0); setEditing(false);}} className="text-xs uppercase tracking-widest text-zinc-500">Annulla</button>
          </>
        ) : (
          <>
            <span className="font-mono-data">{machine.ore_lavoro || 0}h</span>
            <button onClick={()=>setEditing(true)} className="text-xs uppercase tracking-widest text-[#4CAF50] hover:underline" data-testid="machine-ore-edit">Modifica</button>
          </>
        )}
      </dd>
    </div>
  );
};

const PartsTab = ({wo, reload}) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [adding, setAdding] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [manForm, setManForm] = useState({ descrizione: "", quantita: 1, prezzo_unitario: 0, codice_fornitore: "", note: "" });

  const markup = wo.markup_percentuale ?? 30;
  const salesPrice = (r) => {
    if (r.prezzo_vendita != null && r.prezzo_vendita !== "") return r.prezzo_vendita;
    return (r.prezzo_unitario || 0) * (1 + markup / 100);
  };

  const search = async (val) => {
    if (val.length < 1) { setResults([]); return; }
    const { data } = await api.get("/products", { params: { q: val } });
    setResults(data.slice(0, 6));
  };

  const addPart = async (product, qty=1) => {
    setAdding(true);
    try {
      await api.post(`/work-orders/${wo.id}/parts`, { product_id: product.id, quantita: qty });
      toast.success(`${product.descrizione} aggiunto (-${qty} dal magazzino)`);
      setResults([]); setQ(""); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAdding(false); }
  };

  const addManual = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/work-orders/${wo.id}/parts-manual`, {
        descrizione: manForm.descrizione,
        quantita: parseFloat(manForm.quantita) || 1,
        prezzo_unitario: parseFloat(manForm.prezzo_unitario) || 0,
        codice_fornitore: manForm.codice_fornitore,
        note: manForm.note,
      });
      toast.success("Ricambio manuale aggiunto"); setShowManual(false);
      setManForm({ descrizione: "", quantita: 1, prezzo_unitario: 0, codice_fornitore: "", note: "" });
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removePart = async (partId) => {
    if (!window.confirm("Rimuovere? Se proveniva dal magazzino verrà ripristinato.")) return;
    try { await api.delete(`/work-orders/${wo.id}/parts/${partId}`); reload(); toast.success("Rimosso"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="gm-card p-4">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <label className="text-xs font-bold uppercase tracking-widest text-zinc-400">Aggiungi dal magazzino</label>
          <button onClick={()=>setShowManual(!showManual)} className="text-xs uppercase tracking-widest text-[#4CAF50] hover:underline flex items-center gap-1" data-testid="wo-toggle-manual-part">
            <Plus size={12} /> oppure ricambio manuale (non in magazzino)
          </button>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
          <input value={q} onChange={(e)=>{setQ(e.target.value); search(e.target.value);}} placeholder="Codice o descrizione (oppure scansiona)" className="gm-input pl-11" data-testid="wo-part-search" />
        </div>
        {results.length > 0 && (
          <div className="mt-3 border border-zinc-800 divide-y divide-zinc-800/50">
            {results.map(p => (
              <button key={p.id} disabled={p.giacenza<=0||adding} onClick={()=>addPart(p, 1)} className={`w-full text-left p-3 flex items-center justify-between hover:bg-zinc-900 ${p.giacenza<=0?"opacity-40":""}`} data-testid="wo-part-add">
                <div className="min-w-0">
                  <div className="font-mono-data text-xs text-[#4CAF50]">{p.codice_interno}</div>
                  <div className="text-sm">{p.descrizione}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono-data text-white">{p.giacenza}</div>
                  <div className="text-[10px] uppercase text-zinc-500">€ {p.prezzo_acquisto?.toFixed(2)} acq.</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {showManual && (
        <form onSubmit={addManual} className="gm-card p-4 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="wo-manual-part-form">
          <div className="md:col-span-3 flex items-center gap-2 text-xs uppercase tracking-widest text-[#FF9F0A]">
            <AlertTriangle size={14} /> Ricambio NON da magazzino: il prezzo qui sotto è il prezzo finale di vendita (NO ricarico applicato)
          </div>
          <input required placeholder="Descrizione ricambio *" value={manForm.descrizione} onChange={e=>setManForm(s=>({...s,descrizione:e.target.value}))} className="gm-input md:col-span-2" data-testid="manual-part-desc" />
          <input placeholder="Codice fornitore" value={manForm.codice_fornitore} onChange={e=>setManForm(s=>({...s,codice_fornitore:e.target.value}))} className="gm-input font-mono-data" />
          <input type="number" step="0.5" min="0.5" required placeholder="Quantità" value={manForm.quantita} onChange={e=>setManForm(s=>({...s,quantita:e.target.value}))} className="gm-input font-mono-data" data-testid="manual-part-qty" />
          <input type="number" step="0.01" min="0" required placeholder="Prezzo unitario di vendita (€)" value={manForm.prezzo_unitario} onChange={e=>setManForm(s=>({...s,prezzo_unitario:e.target.value}))} className="gm-input font-mono-data" data-testid="manual-part-price" />
          <button type="submit" className="gm-btn-primary h-12 rounded-sm" data-testid="manual-part-submit">Aggiungi ricambio</button>
        </form>
      )}

      <div className="gm-card overflow-x-auto">
        <table className="gm-table">
          <thead><tr>
            <th>Codice</th><th>Descrizione</th><th className="text-right">Q.tà</th>
            <th className="text-right">Acquisto</th><th className="text-right">+%</th>
            <th className="text-right">Vendita</th><th className="text-right">Totale</th>
            <th>Origine</th><th></th>
          </tr></thead>
          <tbody>
            {wo.ricambi?.length === 0 && (<tr><td colSpan="9" className="text-center text-zinc-500 py-6">Nessun ricambio</td></tr>)}
            {wo.ricambi?.map(p => (
              <PartRow key={p.id} p={p} wo={wo} markup={markup} salesPrice={salesPrice} reload={reload} onRemove={removePart} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PartRow = ({p, wo, markup, salesPrice, reload, onRemove}) => {
  const [editing, setEditing] = useState(false);
  const [qty, setQty] = useState(p.quantita);
  const [pv, setPv] = useState(p.prezzo_vendita ?? "");
  const isManual = p.manuale;
  const effSale = salesPrice(p);

  const save = async () => {
    try {
      const payload = { quantita: parseFloat(qty) || 0 };
      if (pv !== "" && pv != null) payload.prezzo_vendita = parseFloat(pv);
      else payload.prezzo_vendita = null;
      await api.put(`/work-orders/${wo.id}/parts/${p.id}`, payload);
      toast.success("Riga aggiornata"); setEditing(false); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <tr data-testid="wo-part-row">
      <td className="font-mono-data text-[#4CAF50]">{isManual ? <span className="gm-badge gm-badge-warning">MAN</span> : p.codice_interno}</td>
      <td>{p.descrizione}{p.codice_fornitore && <span className="text-xs text-zinc-500 ml-2 font-mono-data">{p.codice_fornitore}</span>}</td>
      <td className="text-right font-mono-data">
        {editing ? <input type="number" step="0.5" value={qty} onChange={e=>setQty(e.target.value)} className="bg-transparent border-b border-[#4CAF50] outline-none w-16 text-right" /> : p.quantita}
      </td>
      <td className="text-right font-mono-data text-zinc-500 text-xs">{isManual ? "—" : `€ ${p.prezzo_unitario?.toFixed(2)}`}</td>
      <td className="text-right font-mono-data text-xs text-zinc-400">{isManual ? "—" : `+${markup}%`}</td>
      <td className="text-right font-mono-data">
        {editing ? (
          <input type="number" step="0.01" placeholder={`auto: ${effSale.toFixed(2)}`} value={pv} onChange={e=>setPv(e.target.value)} className="bg-transparent border-b border-[#4CAF50] outline-none w-24 text-right" data-testid="part-price-input" />
        ) : (
          <span className="text-white">€ {effSale.toFixed(2)}</span>
        )}
      </td>
      <td className="text-right font-mono-data font-bold">€ {(effSale * p.quantita).toFixed(2)}</td>
      <td className="text-xs text-zinc-400">{isManual ? "Manuale" : `Magazzino · ${p.added_by||""}`}</td>
      <td className="text-right space-x-1 whitespace-nowrap">
        {editing ? (
          <>
            <button onClick={save} className="text-[#4CAF50] p-1.5 inline-block" data-testid="part-save"><Check size={14}/></button>
            <button onClick={()=>{setQty(p.quantita); setPv(p.prezzo_vendita??""); setEditing(false);}} className="text-zinc-500 p-1.5 inline-block"><X size={14}/></button>
          </>
        ) : (
          <>
            <button onClick={()=>setEditing(true)} className="text-zinc-400 hover:text-white p-1.5 inline-block" data-testid="part-edit"><Edit2 size={14}/></button>
            <button onClick={()=>onRemove(p.id)} className="text-[#FF3B30] p-1.5 inline-block"><Trash2 size={14}/></button>
          </>
        )}
      </td>
    </tr>
  );
};

const TimeTab = ({wo, reload}) => {
  const { user } = useAuth();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ ore: 1, note: "", data: "" });

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ore: parseFloat(form.ore) || 0, note: form.note };
      if (form.data) payload.data = new Date(form.data).toISOString();
      await api.post(`/work-orders/${wo.id}/time-entry`, payload);
      toast.success(`+${form.ore}h registrate`);
      setForm({ ore: 1, note: "", data: "" });
      setAdding(false);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeEntry = async (entryId) => {
    if (!window.confirm("Eliminare questa voce di tempo?")) return;
    try { await api.delete(`/work-orders/${wo.id}/time-entry/${entryId}`); reload(); toast.success("Eliminata"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="gm-card p-4">
        {!adding ? (
          <button onClick={()=>setAdding(true)} className="gm-btn-primary h-11 px-5 rounded-sm flex items-center gap-2" data-testid="wo-add-manual-hours">
            <Plus size={16} /> Aggiungi ore manuali
          </button>
        ) : (
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Ore *</label>
              <input type="number" step="0.25" min="0.25" required value={form.ore} onChange={e=>setForm(s=>({...s,ore:e.target.value}))} className="gm-input font-mono-data text-lg" data-testid="manual-ore-input" autoFocus />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Data/ora (opzionale)</label>
              <input type="datetime-local" value={form.data} onChange={e=>setForm(s=>({...s,data:e.target.value}))} className="gm-input" />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Note (opzionale)</label>
              <input value={form.note} onChange={e=>setForm(s=>({...s,note:e.target.value}))} placeholder="es. trasferta + montaggio" className="gm-input" />
            </div>
            <button type="submit" className="gm-btn-primary h-11 px-4 rounded-sm md:col-span-2 flex items-center justify-center gap-2" data-testid="manual-ore-submit">
              <Check size={16}/> Registra ore
            </button>
            <button type="button" onClick={()=>setAdding(false)} className="h-11 px-4 border border-zinc-700 text-zinc-300 uppercase text-xs tracking-widest font-bold md:col-span-2">Annulla</button>
          </form>
        )}
      </div>

      <div className="gm-card overflow-x-auto">
        <table className="gm-table">
          <thead><tr><th>Tecnico</th><th>Check-in / Data</th><th>Check-out</th><th className="text-right">Ore</th><th>Note</th><th>Tipo</th><th></th></tr></thead>
          <tbody>
            {wo.time_entries?.length === 0 && (<tr><td colSpan="7" className="text-center text-zinc-500 py-6">Nessun intervento registrato</td></tr>)}
            {wo.time_entries?.map(t => (
              <TimeRow key={t.id} t={t} wo={wo} user={user} reload={reload} onDelete={removeEntry} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const TimeRow = ({t, wo, user, reload, onDelete}) => {
  const [editing, setEditing] = useState(false);
  const [ore, setOre] = useState(t.ore || 0);
  const [note, setNote] = useState(t.note || "");
  const canEdit = t.user_id === user.id || user.role === "admin";
  const isOpen = !t.check_out;

  const save = async () => {
    try {
      await api.put(`/work-orders/${wo.id}/time-entry/${t.id}`, { ore: parseFloat(ore) || 0, note });
      toast.success("Ore aggiornate");
      setEditing(false);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <tr data-testid="time-entry-row">
      <td className="font-semibold">{t.user_name}</td>
      <td className="font-mono-data text-xs text-zinc-400">{new Date(t.check_in).toLocaleString("it-IT")}</td>
      <td className="font-mono-data text-xs text-zinc-400">{t.check_out ? new Date(t.check_out).toLocaleString("it-IT") : <span className="text-[#4CAF50]">IN CORSO</span>}</td>
      <td className="text-right font-mono-data font-bold">
        {editing ? (
          <input type="number" step="0.25" value={ore} onChange={e=>setOre(e.target.value)} className="bg-transparent border-b border-[#4CAF50] outline-none w-20 text-right" autoFocus />
        ) : (
          <>{(t.ore || 0).toFixed(2)}h {t.manuale && <span className="ml-1 gm-badge gm-badge-muted">M</span>}</>
        )}
      </td>
      <td className="text-xs text-zinc-400">
        {editing ? (
          <input value={note} onChange={e=>setNote(e.target.value)} className="bg-transparent border-b border-[#4CAF50] outline-none w-full text-xs" />
        ) : (t.note || "—")}
      </td>
      <td className="text-xs text-zinc-500">{t.manuale ? "Manuale" : "Check-in"}</td>
      <td className="text-right space-x-1 whitespace-nowrap">
        {!isOpen && canEdit && (
          editing ? (
            <>
              <button onClick={save} className="text-[#4CAF50] p-1.5 inline-block" data-testid="time-entry-save"><Check size={14}/></button>
              <button onClick={()=>{setOre(t.ore||0); setNote(t.note||""); setEditing(false);}} className="text-zinc-500 p-1.5 inline-block"><X size={14}/></button>
            </>
          ) : (
            <>
              <button onClick={()=>setEditing(true)} className="text-zinc-400 hover:text-white p-1.5 inline-block" data-testid="time-entry-edit"><Edit2 size={14}/></button>
              <button onClick={()=>onDelete(t.id)} className="text-[#FF3B30] hover:bg-[#FF3B30]/10 p-1.5 inline-block" data-testid="time-entry-delete"><Trash2 size={14}/></button>
            </>
          )
        )}
      </td>
    </tr>
  );
};

const PhotosTab = ({wo, reload}) => {
  const upload = (fase) => async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) { toast.error("Foto max 1.5MB"); return; }
    const r = new FileReader();
    r.onload = async () => {
      try { await api.post(`/work-orders/${wo.id}/photos`, { fase, foto: r.result }); reload(); toast.success("Foto aggiunta"); }
      catch (err) { toast.error("Errore upload"); }
    };
    r.readAsDataURL(file);
  };

  return (
    <div className="space-y-5">
      {[["prima","Prima dell'intervento","foto_iniziali"],["durante","Durante","foto_durante"],["dopo","Foto finali","foto_finali"]].map(([k, label, field])=>(
        <div key={k} className="gm-card p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-lg font-bold uppercase tracking-tight">{label}</h3>
            <label className="text-xs uppercase tracking-widest text-[#4CAF50] cursor-pointer flex items-center gap-1" data-testid={`wo-photo-${k}`}>
              <Camera size={14} /> Aggiungi
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={upload(k)} />
            </label>
          </div>
          {wo[field]?.length === 0 ? <div className="text-sm text-zinc-500">Nessuna foto</div> : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {wo[field]?.map((f,i)=>(
                <a key={i} href={f} target="_blank" rel="noreferrer">
                  <img src={f} alt="" className="h-28 w-full object-cover border border-zinc-800 hover:border-[#4CAF50]" />
                </a>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const CostsTab = ({wo, machine, updateField, reload}) => {
  const sigRef = useRef(null);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({x:0, y:0});

  const start = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    last.current = { x: t.clientX - r.left, y: t.clientY - r.top };
    drawing.current = true;
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const r = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    const x = t.clientX - r.left, y = t.clientY - r.top;
    const ctx = canvasRef.current.getContext("2d");
    ctx.strokeStyle = "#fff"; ctx.lineWidth = 2; ctx.lineCap = "round";
    ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(x, y); ctx.stroke();
    last.current = { x, y };
  };
  const end = () => { drawing.current = false; };
  const clearSig = () => { canvasRef.current.getContext("2d").clearRect(0,0,canvasRef.current.width, canvasRef.current.height); };

  const saveSig = async () => {
    const data = canvasRef.current.toDataURL("image/png");
    try { await api.post(`/work-orders/${wo.id}/sign`, { firma_cliente: data, nome_firmatario: sigRef.current?.value || "" }); reload(); toast.success("Firma salvata. Scheda completata."); }
    catch (e) { toast.error("Errore"); }
  };

  const printReport = async () => {
    const markup = wo.markup_percentuale ?? 30;
    const sale = (r) => (r.prezzo_vendita != null && r.prezzo_vendita !== "") ? r.prezzo_vendita : (r.prezzo_unitario || 0) * (1 + markup/100);
    const fmt = (n) => `€ ${(n||0).toLocaleString("it-IT", {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;

    // Load logo
    let logoData = null;
    try {
      const resp = await fetch("/assets/germac-logo.png");
      const blob = await resp.blob();
      logoData = await new Promise(res => { const r = new FileReader(); r.onload = ()=>res(r.result); r.readAsDataURL(blob); });
    } catch (e) { /* logo opzionale */ }

    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const W = 210, M = 14;
    const C_GREEN = [76, 175, 80];
    const C_DARK = [25, 28, 34];
    const C_GREY = [110, 116, 128];
    const C_LINE = [220, 220, 220];

    // ===== HEADER BAND =====
    doc.setFillColor(...C_DARK);
    doc.rect(0, 0, W, 32, "F");
    if (logoData) {
      doc.setFillColor(255,255,255);
      doc.roundedRect(M, 6, 22, 22, 1, 1, "F");
      doc.addImage(logoData, "PNG", M+2, 8, 18, 18);
    }
    doc.setTextColor(255,255,255);
    doc.setFont("helvetica", "bold").setFontSize(20);
    doc.text("RAPPORTO DI INTERVENTO", M+28, 16);
    doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(180,185,195);
    doc.text("GERMAC S.R.L. — Macchine da lavoro", M+28, 22);
    doc.text("germac.it", M+28, 27);
    // Right side: WO number
    doc.setTextColor(...C_GREEN).setFont("helvetica", "bold").setFontSize(11);
    doc.text(wo.numero, W-M, 14, { align: "right" });
    doc.setTextColor(200,200,210).setFontSize(8).setFont("helvetica", "normal");
    doc.text(`Data: ${new Date(wo.created_at).toLocaleDateString("it-IT")}`, W-M, 20, { align: "right" });
    doc.text(`Stato: ${wo.stato.replaceAll("_"," ").toUpperCase()}`, W-M, 25, { align: "right" });
    if (wo.numero_fattura) doc.text(`Fattura n°: ${wo.numero_fattura}${wo.data_fatturazione ? ` del ${new Date(wo.data_fatturazione).toLocaleDateString("it-IT")}` : ""}`, W-M, 30, { align: "right" });

    // ===== ACCENT BAR =====
    doc.setFillColor(...C_GREEN); doc.rect(0, 32, W, 1.5, "F");

    let y = 42;

    // ===== CLIENTE / MACCHINA boxes =====
    const cardH = 30;
    const cardW = (W - M*2 - 4) / 2;
    // Cliente
    doc.setDrawColor(...C_LINE); doc.setLineWidth(0.3);
    doc.roundedRect(M, y, cardW, cardH, 1.5, 1.5);
    doc.setFillColor(...C_GREEN).rect(M, y, cardW, 5, "F");
    doc.setTextColor(255,255,255).setFont("helvetica", "bold").setFontSize(8);
    doc.text("CLIENTE", M+2, y+3.5);
    doc.setTextColor(...C_DARK).setFontSize(10).setFont("helvetica", "bold");
    doc.text(doc.splitTextToSize(wo.customer_name || "—", cardW-4), M+3, y+10);
    doc.setFont("helvetica", "normal").setFontSize(8).setTextColor(...C_GREY);
    let yC = y + 16;
    if (machine?.customer?.referente) { doc.text(`Ref: ${machine.customer.referente}`, M+3, yC); yC += 4; }
    if (machine?.customer?.telefono) { doc.text(`Tel: ${machine.customer.telefono}`, M+3, yC); yC += 4; }
    if (machine?.customer?.email) { doc.text(machine.customer.email, M+3, yC); }
    // Macchina
    const xM = M + cardW + 4;
    doc.setDrawColor(...C_LINE);
    doc.roundedRect(xM, y, cardW, cardH, 1.5, 1.5);
    doc.setFillColor(...C_GREEN).rect(xM, y, cardW, 5, "F");
    doc.setTextColor(255,255,255).setFont("helvetica", "bold").setFontSize(8);
    doc.text("MACCHINA", xM+2, y+3.5);
    doc.setTextColor(...C_DARK).setFontSize(10).setFont("helvetica", "bold");
    doc.text(`${machine?.marca || ""} ${machine?.modello || ""}`.trim() || "—", xM+3, y+10);
    doc.setFont("helvetica", "normal").setFontSize(8).setTextColor(...C_GREY);
    doc.text(`Matricola: ${machine?.matricola || "—"}`, xM+3, y+16);
    if (machine?.targa) doc.text(`Targa: ${machine.targa}`, xM+3, y+20);
    doc.text(`Ore lavoro: ${machine?.ore_lavoro || 0}h`, xM+3, y+24);
    if (machine?.garanzia_scadenza) {
      const inWarranty = new Date(machine.garanzia_scadenza) >= new Date();
      doc.setTextColor(inWarranty ? 76 : 110, inWarranty ? 175 : 116, inWarranty ? 80 : 128);
      doc.text(`Garanzia ${inWarranty ? "ATTIVA" : "scaduta"} (${machine.garanzia_scadenza})`, xM+3, y+28);
    }

    y += cardH + 6;

    // ===== PROBLEMA RIFERITO =====
    doc.setTextColor(...C_DARK).setFont("helvetica", "bold").setFontSize(10);
    doc.text("PROBLEMA RIFERITO", M, y);
    doc.setDrawColor(...C_GREEN).setLineWidth(0.8);
    doc.line(M, y+1.5, M+50, y+1.5);
    y += 6;
    doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(60,65,75);
    const problemLines = doc.splitTextToSize(wo.descrizione_problema || "—", W - M*2);
    doc.text(problemLines, M, y);
    y += problemLines.length * 4 + 5;

    // ===== ATTIVITÀ SVOLTE =====
    if (wo.attivita_svolte) {
      doc.setTextColor(...C_DARK).setFont("helvetica", "bold").setFontSize(10);
      doc.text("ATTIVITÀ SVOLTE", M, y);
      doc.setDrawColor(...C_GREEN).setLineWidth(0.8); doc.line(M, y+1.5, M+45, y+1.5);
      y += 6;
      doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(60,65,75);
      const actLines = doc.splitTextToSize(wo.attivita_svolte, W - M*2);
      doc.text(actLines, M, y);
      y += actLines.length * 4 + 5;
    }

    // ===== RICAMBI TABLE =====
    if (wo.ricambi?.length > 0) {
      doc.setTextColor(...C_DARK).setFont("helvetica", "bold").setFontSize(10);
      doc.text("RICAMBI UTILIZZATI", M, y);
      doc.setDrawColor(...C_GREEN).setLineWidth(0.8); doc.line(M, y+1.5, M+50, y+1.5);
      y += 3;
      autoTable(doc, {
        startY: y,
        margin: { left: M, right: M },
        head: [["Codice", "Descrizione", "Q.tà", "€/unità", "Totale"]],
        body: wo.ricambi.map(r => [
          r.manuale ? "MAN" : r.codice_interno,
          r.descrizione,
          String(r.quantita),
          fmt(sale(r)),
          fmt(sale(r) * r.quantita),
        ]),
        theme: "grid",
        styles: { font: "helvetica", fontSize: 8.5, cellPadding: 2, lineColor: C_LINE, textColor: C_DARK },
        headStyles: { fillColor: C_DARK, textColor: 255, fontStyle: "bold", fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 24, font: "courier", fontStyle: "bold" },
          2: { cellWidth: 14, halign: "right", font: "courier" },
          3: { cellWidth: 24, halign: "right", font: "courier" },
          4: { cellWidth: 28, halign: "right", font: "courier", fontStyle: "bold" },
        },
        alternateRowStyles: { fillColor: [248, 249, 250] },
      });
      y = doc.lastAutoTable.finalY + 5;
    }

    // ===== TIME ENTRIES (if any) =====
    if (wo.time_entries?.length > 0) {
      const totH = wo.ore_totali || 0;
      if (totH > 0) {
        doc.setTextColor(...C_DARK).setFont("helvetica", "bold").setFontSize(10);
        doc.text("ORE LAVORATE", M, y);
        doc.setDrawColor(...C_GREEN).setLineWidth(0.8); doc.line(M, y+1.5, M+35, y+1.5);
        y += 3;
        autoTable(doc, {
          startY: y, margin: { left: M, right: M },
          head: [["Tecnico", "Data", "Ore", "Note"]],
          body: wo.time_entries.filter(t=>t.check_out).map(t => [
            t.user_name,
            new Date(t.check_in).toLocaleDateString("it-IT"),
            `${(t.ore||0).toFixed(2)}h`,
            t.note || "",
          ]),
          theme: "grid",
          styles: { font: "helvetica", fontSize: 8.5, cellPadding: 2, lineColor: C_LINE, textColor: C_DARK },
          headStyles: { fillColor: C_DARK, textColor: 255, fontStyle: "bold", fontSize: 8 },
          columnStyles: { 2: { halign: "right", font: "courier", fontStyle: "bold" } },
          alternateRowStyles: { fillColor: [248, 249, 250] },
        });
        y = doc.lastAutoTable.finalY + 5;
      }
    }

    // Page break check
    if (y > 230) { doc.addPage(); y = 20; }

    // ===== RIEPILOGO COSTI =====
    const sumX = W - M - 80;
    const sumW = 80;
    doc.setDrawColor(...C_LINE).setLineWidth(0.3);
    doc.roundedRect(sumX, y, sumW, 38, 1.5, 1.5);
    doc.setFillColor(245, 247, 248); doc.rect(sumX, y, sumW, 38, "F");
    doc.setDrawColor(...C_LINE); doc.roundedRect(sumX, y, sumW, 38, 1.5, 1.5);
    doc.setFont("helvetica", "bold").setFontSize(8).setTextColor(...C_DARK);
    doc.text("RIEPILOGO COSTI", sumX + 3, y + 5);
    doc.setLineWidth(0.2).line(sumX+3, y+6, sumX+sumW-3, y+6);

    doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(...C_GREY);
    const labels = [
      [`Manodopera (${(wo.ore_totali||0).toFixed(2)}h × ${fmt(wo.costo_orario||0)})`, wo.manodopera_costo || 0],
      [`Ricambi`, wo.ricambi_costo || 0],
      [`Trasferta${wo.km_viaggio ? ` (${wo.km_viaggio} km)` : ""}`, wo.costo_trasferta || 0],
      [`Costi vari`, wo.costi_vari || 0],
    ];
    let yC2 = y + 11;
    labels.forEach(([lbl, val]) => {
      doc.setTextColor(...C_GREY).text(lbl, sumX+3, yC2);
      doc.setTextColor(...C_DARK).setFont("courier", "normal").text(fmt(val), sumX+sumW-3, yC2, { align: "right" });
      doc.setFont("helvetica", "normal");
      yC2 += 4.5;
    });

    // TOTAL BAR
    doc.setFillColor(...C_GREEN); doc.rect(sumX, y+38+0.5, sumW, 10, "F");
    doc.setTextColor(255,255,255).setFont("helvetica", "bold").setFontSize(9);
    doc.text("TOTALE", sumX + 3, y+38+6.5);
    doc.setFontSize(13).setFont("courier", "bold");
    doc.text(fmt(wo.totale || 0), sumX + sumW - 3, y+38+7, { align: "right" });

    // Tipo intervento (left of total box)
    doc.setTextColor(...C_DARK).setFont("helvetica", "bold").setFontSize(8);
    doc.text("TIPO INTERVENTO", M, y+5);
    doc.setFont("helvetica", "normal").setFontSize(10).setTextColor(...C_GREEN);
    doc.text({pagamento: "A PAGAMENTO", garanzia_cliente: "GARANZIA CLIENTE", garanzia_costruttore: "GARANZIA COSTRUTTORE"}[wo.tipo_garanzia] || "—", M, y+11);
    doc.setTextColor(...C_DARK).setFont("helvetica", "bold").setFontSize(8);
    doc.text("URGENZA", M, y+17);
    doc.setFont("helvetica", "normal").setFontSize(10).setTextColor(...C_GREY);
    doc.text((wo.urgenza||"").toUpperCase(), M, y+23);

    y += 55;

    // ===== FIRMA =====
    if (y > 240) { doc.addPage(); y = 20; }
    doc.setDrawColor(...C_LINE).setLineWidth(0.3);
    // Tecnico
    doc.line(M, y+15, M+70, y+15);
    doc.setFont("helvetica", "normal").setFontSize(8).setTextColor(...C_GREY);
    doc.text("FIRMA TECNICO", M, y+19);
    if (wo.firma_tecnico) doc.addImage(wo.firma_tecnico, "PNG", M, y, 60, 15);
    // Cliente
    doc.line(W-M-70, y+15, W-M, y+15);
    doc.text("FIRMA CLIENTE", W-M-70, y+19);
    if (wo.firma_cliente) doc.addImage(wo.firma_cliente, "PNG", W-M-70, y, 60, 15);
    if (wo.nome_firmatario) {
      doc.setFontSize(7).text(wo.nome_firmatario, W-M-70, y+23);
    }

    // ===== FOOTER =====
    const pageCount = doc.internal.pages.length - 1;
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setDrawColor(...C_LINE); doc.setLineWidth(0.3);
      doc.line(M, 285, W-M, 285);
      doc.setFont("helvetica", "normal").setFontSize(7).setTextColor(...C_GREY);
      doc.text(`GERMAC S.R.L. — Rapporto ${wo.numero}`, M, 290);
      doc.text(`Generato il ${new Date().toLocaleString("it-IT")}`, W/2, 290, { align: "center" });
      doc.text(`Pagina ${i} di ${pageCount}`, W-M, 290, { align: "right" });
    }

    doc.save(`Rapporto_${wo.numero}_${wo.customer_name?.replaceAll(" ","_")||""}.pdf`);
  };

  return (
    <div className="space-y-5">
      <div className="gm-card p-5">
        <h3 className="font-display text-xl font-bold uppercase tracking-tight mb-3">Costi</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Costo label="Costo orario (€/h)" value={wo.costo_orario} onSave={v=>updateField("costo_orario", parseFloat(v)||0)} />
          <Costo label="Ricarico ricambi (%)" value={wo.markup_percentuale} onSave={v=>updateField("markup_percentuale", parseFloat(v)||0)} />
          <Costo label="Costo trasferta (€)" value={wo.costo_trasferta} onSave={v=>updateField("costo_trasferta", parseFloat(v)||0)} />
          <Costo label="Costi vari (€)" value={wo.costi_vari} onSave={v=>updateField("costi_vari", parseFloat(v)||0)} />
        </div>
        <div className="text-xs text-zinc-500 mt-2">
          Il ricarico % si applica automaticamente al prezzo di acquisto dei ricambi da magazzino. I ricambi inseriti manualmente usano il prezzo fissato (nessun ricarico). Il costo trasferta viene calcolato automaticamente dai km inseriti (tab Info) × tariffa €/km; puoi comunque modificarlo qui manualmente.
        </div>
        <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
          <div className="text-zinc-400">Manodopera ({(wo.ore_totali||0).toFixed(2)}h)</div><div className="text-right font-mono-data">€ {(wo.manodopera_costo||0).toFixed(2)}</div>
          <div className="text-zinc-400">Ricambi</div><div className="text-right font-mono-data">€ {(wo.ricambi_costo||0).toFixed(2)}</div>
          <div className="text-zinc-400">Trasferta</div><div className="text-right font-mono-data">€ {(wo.costo_trasferta||0).toFixed(2)}</div>
          <div className="text-zinc-400">Vari</div><div className="text-right font-mono-data">€ {(wo.costi_vari||0).toFixed(2)}</div>
          <div className="text-white font-bold border-t border-zinc-800 pt-2">TOTALE</div>
          <div className="text-right font-mono-data font-bold text-[#4CAF50] text-lg border-t border-zinc-800 pt-2">€ {(wo.totale||0).toFixed(2)}</div>
        </div>
      </div>

      <div className="gm-card p-5">
        <h3 className="font-display text-xl font-bold uppercase tracking-tight mb-3">Firma cliente</h3>
        {wo.firma_cliente ? (
          <div>
            <img src={wo.firma_cliente} alt="firma" className="bg-white p-2 h-32 border border-zinc-800" />
            <div className="text-sm text-zinc-400 mt-2">Firmato da: <b className="text-white">{wo.nome_firmatario || "—"}</b></div>
          </div>
        ) : (
          <div>
            <input ref={sigRef} placeholder="Nome firmatario" className="gm-input mb-2" data-testid="wo-firmatario" />
            <canvas ref={canvasRef} width={500} height={150}
              className="bg-zinc-900 border border-zinc-700 w-full touch-none rounded-sm"
              onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
              onTouchStart={start} onTouchMove={move} onTouchEnd={end}
              data-testid="wo-sign-canvas"
            />
            <div className="flex gap-2 mt-2">
              <button onClick={clearSig} className="h-10 px-4 border border-zinc-700 uppercase text-xs tracking-widest">Pulisci</button>
              <button onClick={saveSig} className="gm-btn-primary h-10 px-5 rounded-sm flex items-center gap-2" data-testid="wo-sign-save"><PenLine size={14}/> Salva firma</button>
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-3 flex-wrap">
        <button onClick={printReport} className="gm-btn-primary h-12 px-6 rounded-sm flex items-center gap-2" data-testid="wo-print-report">
          <FileText size={18} /> Genera Rapportino PDF
        </button>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/work-orders/${wo.id}/xml-fattura`}
          target="_blank" rel="noreferrer"
          className="h-12 px-6 border-2 border-[#4CAF50] text-[#4CAF50] uppercase tracking-widest font-bold rounded-sm flex items-center gap-2 hover:bg-[#4CAF50]/10"
          data-testid="wo-export-xml-fattura"
          onClick={(e)=>{
            const token = localStorage.getItem("germac_token");
            if (token) {
              e.preventDefault();
              fetch(e.currentTarget.href, { headers: { Authorization: `Bearer ${token}` }})
                .then(r=>r.blob()).then(b=>{
                  const url = URL.createObjectURL(b);
                  const a = document.createElement("a");
                  a.href = url; a.download = `Fattura_${wo.numero}.xml`; a.click();
                });
            }
          }}
        >
          <FileText size={18} /> Export XML Fattura
        </a>
      </div>
    </div>
  );
};

const Costo = ({label, value, onSave}) => {
  const [v, setV] = useState(value ?? 0);
  useEffect(()=>setV(value??0), [value]);
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</label>
      <input type="number" step="0.01" value={v} onChange={e=>setV(e.target.value)} onBlur={()=>onSave(v)} className="gm-input font-mono-data" />
    </div>
  );
};
